import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import { PageHeader } from '../components/PageHeader';
import { useChecklist } from '../hooks/useOps';
import { opsApi } from '../api/client';
import type { ItemResult } from '../api/types';
import { formatDate, humanizeStatus, statusColor } from '../lib/format';

const RESULT_COLOR: Record<ItemResult, 'default' | 'success' | 'error' | 'info'> = {
  pending: 'default',
  pass: 'success',
  fail: 'error',
  na: 'info',
};

export function QualificationPage() {
  const { id: projectId, checklistId } = useParams<{ id: string; checklistId: string }>();
  const navigate = useNavigate();
  const { data: checklist, isLoading, error } = useChecklist(checklistId);

  const downloadPdf = async () => {
    if (!checklistId) return;
    try {
      const blob = await opsApi.exportChecklistPdf(checklistId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `checklist-${checklistId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      window.alert((e as Error).message);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !checklist) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {(error as Error)?.message ?? 'Checklist not found'}
      </Alert>
    );
  }

  const items = (checklist.items ?? []).sort((a, b) => a.sequence - b.sequence);
  const passCount = items.filter((i) => i.result === 'pass').length;
  const failCount = items.filter((i) => i.result === 'fail').length;
  const pendingCount = items.filter((i) => i.result === 'pending').length;

  return (
    <Box>
      <PageHeader
        title={`${checklist.type.toUpperCase()} Checklist`}
        subtitle={`Status: ${humanizeStatus(checklist.status)}`}
        action={
          <Stack direction="row" gap={1}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadPdf}
            >
              Export PDF
            </Button>
            <Button variant="outlined" onClick={() => navigate(`/projects/${projectId}/quality`)}>
              ← Quality
            </Button>
          </Stack>
        }
      />

      {/* Summary chips */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
          <Chip label={`${passCount} pass`} color="success" />
          <Chip label={`${failCount} fail`} color="error" />
          <Chip label={`${pendingCount} pending`} color="default" />
          <Box flexGrow={1} />
          {checklist.startedAt && (
            <Typography variant="caption" color="text.secondary">
              Started: {formatDate(checklist.startedAt)}
            </Typography>
          )}
          {checklist.completedAt && (
            <Typography variant="caption" color="text.secondary">
              Completed: {formatDate(checklist.completedAt)}
            </Typography>
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={48}>#</TableCell>
              <TableCell>Description</TableCell>
              <TableCell width={90}>Result</TableCell>
              <TableCell>Note</TableCell>
              <TableCell width={110}>Checked</TableCell>
              <TableCell width={60}>Photos</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.sequence}</TableCell>
                <TableCell>{item.description}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={item.result}
                    color={RESULT_COLOR[item.result] ?? 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {item.note ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {item.checkedAt ? formatDate(item.checkedAt) : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {(item.photos ?? []).length > 0 ? (item.photos ?? []).length : '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" variant="body2">
                    No items recorded yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
